/**
 * Roster Component - Refactored with Service-Oriented Architecture
 * 
 * This is the PROPERLY refactored version that uses:
 * - Service layer for data management (hooks handle all useEffect patterns)
 * - Custom hooks for business logic (contain proper useEffect implementations)
 * - Modern React patterns and performance optimizations
 * - Clean separation of concerns
 * - NO legacy silent refresh code
 * 
 * CRITICAL: The useEffect patterns are implemented in the HOOKS, not in this component.
 * This component focuses on presentation while hooks handle data management.
 */

import React, { useState, useCallback, useMemo, useReducer, Suspense, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  FileDownload as FileDownloadIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Edit as EditIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import * as api from '../api/api';

// Import refactored services and hooks - THESE CONTAIN ALL THE useEffect LOGIC
import { useRosterData } from '../features/Roster/hooks/useRosterData';
import { useAllocationCalculations } from '../features/Roster/hooks/useAllocationCalculations';
import { useMemberFiltering } from '../features/Roster/hooks/useMemberFiltering';
import { RosterExportService } from '../features/Roster/services/RosterExportService';

// Import utilities and constants
import {
  MONTHS,
  FUNCTION_OPTIONS,
  DEVELOPER_FOCUS_OPTIONS,
  getLocationDisplayName
} from '../features/Roster/utils/rosterCalculations';

// Import filter utilities
import { filterReducer, initialFilterState, getActiveFiltersCount } from '../utils/filterUtils';
import { markRatesUpdated } from '../utils/cacheInvalidation';

// Lazy load heavy components for better performance
const AddMemberModal = React.lazy(() => import('./shared/AddMemberModal'));
const AllocationEditingModal = React.lazy(() => import('./shared/AllocationEditingModal'));

// Skeleton loading components
const MemberRowSkeleton = () => (
  <TableRow>
    <TableCell><div style={{ width: 40, height: 20, backgroundColor: '#f0f0f0', borderRadius: 4 }} /></TableCell>
    <TableCell><div style={{ width: '80%', height: 20, backgroundColor: '#f0f0f0', borderRadius: 4 }} /></TableCell>
    <TableCell><div style={{ width: 60, height: 20, backgroundColor: '#f0f0f0', borderRadius: 4 }} /></TableCell>
    <TableCell><div style={{ width: '90%', height: 20, backgroundColor: '#f0f0f0', borderRadius: 4 }} /></TableCell>
    <TableCell><div style={{ width: '90%', height: 20, backgroundColor: '#f0f0f0', borderRadius: 4 }} /></TableCell>
    <TableCell><div style={{ width: '90%', height: 20, backgroundColor: '#f0f0f0', borderRadius: 4 }} /></TableCell>
    <TableCell><div style={{ width: 80, height: 20, backgroundColor: '#f0f0f0', borderRadius: 4 }} /></TableCell>
    <TableCell><div style={{ width: 80, height: 24, backgroundColor: '#f0f0f0', borderRadius: 4 }} /></TableCell>
    {MONTHS.map((month, idx) => (
      <TableCell key={idx} align="center">
        <div style={{ width: 50, height: 20, backgroundColor: '#f0f0f0', borderRadius: 4, margin: '0 auto' }} />
      </TableCell>
    ))}
  </TableRow>
);

const FilterSkeleton = () => (
  <Paper elevation={2} sx={{ mb: 3, p: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
      {[...Array(6)].map((_, i) => (
        <div key={i} style={{ width: 120, height: 40, backgroundColor: '#f0f0f0', borderRadius: 4 }} />
      ))}
    </Box>
  </Paper>
);

// Enhanced Filter Section Component
const FilterSection = React.memo(({
  filterState,
  dispatch,
  companyOptions,
  locationOptions,
  availableOutcomes,
  availableApplications,
  availableTags,
  availableDepartments,
  year,
  setYear,
  activeFiltersCount,
  itOwnerFilter,
  setItOwnerFilter,
  itOwners
}) => {
  return (
    <Paper elevation={2} sx={{ mb: 3, p: 2 }}>
      {/* First Row - Basic Filters */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        {/* Search Field */}
        <TextField
          label="Search Members"
          value={filterState.searchName}
          onChange={e => dispatch({ type: 'SET_SEARCH', payload: e.target.value })}
          size="small"
          sx={{ minWidth: 200 }}
          placeholder="Search by name..."
        />
        
                {/* Company Filter */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            multiple
            value={filterState.companyFilter}
            onChange={e => dispatch({ type: 'SET_COMPANY_FILTER', payload: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value })}
            displayEmpty
            renderValue={(selected) => {
              if (selected.length === 0) return 'All Companies';
              if (selected.length === 1) return selected[0];
              return `${selected.length} companies`;
            }}
          >
            {companyOptions.map(company => (
              <MenuItem key={company} value={company}>
                {company}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
                {/* Function Filter */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            multiple
            value={filterState.functionFilter}
            onChange={e => dispatch({ type: 'SET_FUNCTION_FILTER', payload: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value })}
            displayEmpty
            renderValue={(selected) => {
              if (selected.length === 0) return 'All Functions';
              if (selected.length === 1) return selected[0];
              return `${selected.length} functions`;
            }}
          >
            {FUNCTION_OPTIONS.map(func => (
              <MenuItem key={func} value={func}>
                {func}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
                {/* Location Filter */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            multiple
            value={filterState.locationFilter}
            onChange={e => dispatch({ type: 'SET_LOCATION_FILTER', payload: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value })}
            displayEmpty
            renderValue={(selected) => {
              if (selected.length === 0) return 'All Locations';
              if (selected.length === 1) return locationOptions.find(loc => loc.code === selected[0])?.display || selected[0];
              return `${selected.length} locations`;
            }}
          >
            {locationOptions.map(location => (
              <MenuItem key={location.code} value={location.code}>
                {location.display}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
                {/* Year Filter */}
        <FormControl size="small" sx={{ minWidth: 80 }}>
          <Select
            value={year}
            onChange={e => setYear(Number(e.target.value))}
            renderValue={(selected) => `Year ${selected}`}
          >
            {[2025, 2026, 2027].map(y => (
              <MenuItem key={y} value={y}>{y}</MenuItem>
            ))}
          </Select>
        </FormControl>
        
        {/* Active Filters Count */}
        <Chip 
          label={`${activeFiltersCount} active filters`} 
          size="small" 
          color={activeFiltersCount > 0 ? 'primary' : 'default'}
        />
        
      </Box>
      
      {/* Second Row - Advanced Filters */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                {/* Outcome Filter */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            multiple
            value={filterState.outcomeFilter}
            onChange={e => dispatch({ type: 'SET_OUTCOME_FILTER', payload: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value })}
            displayEmpty
            renderValue={(selected) => {
              if (selected.length === 0) return 'All Outcomes';
              if (selected.length === 1) return selected[0];
              return `${selected.length} outcomes`;
            }}
          >
            <MenuItem value="all">All Outcomes</MenuItem>
            {availableOutcomes.map(outcome => (
              <MenuItem key={outcome} value={outcome}>
                {outcome}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* IT Owner Filter */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <Select
            value={itOwnerFilter}
            onChange={e => setItOwnerFilter(e.target.value)}
            displayEmpty
            renderValue={(selected) => {
              if (!selected) return 'All IT Owners';
              const owner = itOwners.find(o => o.id === selected);
              return owner ? owner.name : 'All IT Owners';
            }}
          >
            <MenuItem value="">All IT Owners</MenuItem>
            {itOwners.map(owner => (
              <MenuItem key={owner.id} value={owner.id}>{owner.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

                {/* Application Filter */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            multiple
            value={filterState.applicationFilter}
            onChange={e => dispatch({ type: 'SET_APPLICATION_FILTER', payload: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value })}
            displayEmpty
            renderValue={(selected) => {
              if (selected.length === 0) return 'All Applications';
              if (selected.length === 1) return selected[0];
              return `${selected.length} apps`;
            }}
          >
            <MenuItem value="all">All Applications</MenuItem>
            {availableApplications.map(app => (
              <MenuItem key={app} value={app}>
                {app}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
                {/* Tag Filter */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            multiple
            value={filterState.tagFilter}
            onChange={e => dispatch({ type: 'SET_TAG_FILTER', payload: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value })}
            displayEmpty
            renderValue={(selected) => {
              if (selected.length === 0) return 'All Tags';
              if (selected.length === 1) return selected[0];
              return `${selected.length} tags`;
            }}
          >
            <MenuItem value="all">All Tags</MenuItem>
            {availableTags.map(tag => (
              <MenuItem key={tag.id || tag} value={tag.name || tag}>
                {tag.name || tag}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
                        {/* Department Filter */}
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <Select
            multiple
            value={filterState.departmentFilter || []}
            onChange={e => dispatch({ type: 'SET_DEPARTMENT_FILTER', payload: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value })}
            displayEmpty
            renderValue={(selected) => {
              if (selected.length === 0) return 'All Departments';
              if (selected.length === 1) return selected[0];
              return `${selected.length} departments`;
            }}
          >
            {availableDepartments.map(dept => (
              <MenuItem key={dept} value={dept}>
                {dept}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

                {/* Allocation Type Filter */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            multiple
            value={filterState.allocationTypeFilter}
            onChange={e => dispatch({ type: 'SET_ALLOCATION_TYPE_FILTER', payload: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value })}
            displayEmpty
            renderValue={(selected) => {
              if (selected.length === 0) return 'Show None';
              if (selected.length === 1) return selected[0] === 'staffed' ? 'Show Staffed' : 'Show Unassigned';
              if (selected.length === 2) return 'Show Both';
              return `Show ${selected.length} types`;
            }}
          >
            <MenuItem value="staffed">Staffed Members</MenuItem>
            <MenuItem value="unassigned">Unassigned Roles</MenuItem>
          </Select>
        </FormControl>
        
                {/* Allocation Status Filter */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            multiple
            value={filterState.allocationStatusFilter}
            onChange={e => dispatch({ type: 'SET_ALLOCATION_STATUS_FILTER', payload: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value })}
            displayEmpty
            renderValue={(selected) => {
              if (selected.length === 0) return 'All Statuses';
              if (selected.length === 1) return selected[0];
              return `${selected.length} statuses`;
            }}
          >
            <MenuItem value="Not Allocated">Not Allocated</MenuItem>
            <MenuItem value="Under Allocated">Under Allocated</MenuItem>
            <MenuItem value="Fully Allocated">Fully Allocated</MenuItem>
            <MenuItem value="Over Allocated">Over Allocated</MenuItem>
            <MenuItem value="Unassigned Role">Unassigned Role</MenuItem>
          </Select>
        </FormControl>
        
        {/* Active Filters Count */}
        <Chip 
          label={`${activeFiltersCount} active filters`} 
          size="small" 
          color={activeFiltersCount > 0 ? 'primary' : 'default'}
        />
        
        {/* Clear All Button */}
        <Button
          size="small"
          onClick={() => dispatch({ type: 'CLEAR_ALL_FILTERS' })}
          disabled={activeFiltersCount === 0}
          color="error"
        >
          Clear All
        </Button>
      </Box>
    </Paper>
  );
});

// Simple allocation cell component
const AllocationCell = React.memo(({ value, isUnassigned = false }) => {
  const getColor = (val) => {
    if (val > 100) return 'error';
    if (val === 100) return 'success';
    if (val > 0) return 'info';
    return 'default';
  };

  return (
    <TableCell align="center" sx={{ p: '2px !important' }}>
      <Chip
        label={value > 0 ? `${value}%` : '-'}
        size="small"
        color={getColor(value)}
        variant={isUnassigned ? 'outlined' : 'filled'}
        sx={{ 
          minWidth: 35,
          height: 16,
          fontSize: '0.6rem',
          '& .MuiChip-label': { px: 0.25 }
        }}
      />
    </TableCell>
  );
});

// Main Roster Component - FOCUSED ON PRESENTATION
function Roster({ onBackToHome, isReadOnly = false, isActive = true, selectedDepartment = '' }) {
  const theme = useTheme();
  
  // Basic component state
  const [year, setYear] = useState(2026);
  const [addMemberModalOpen, setAddMemberModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [allocationModalOpen, setAllocationModalOpen] = useState(false);
  const [editingMemberAllocations, setEditingMemberAllocations] = useState(null);
  const [editingMemberOutcomes, setEditingMemberOutcomes] = useState(null);
  const [expandedMembers, setExpandedMembers] = useState(new Set());

  // Filter states using useReducer for better performance
  const [filterState, dispatch] = useReducer(filterReducer, initialFilterState);
  
  // Calculate active filters count
  const activeFiltersCount = useMemo(() => getActiveFiltersCount(filterState), [filterState]);

  // CRITICAL: Only ONE useEffect in main component - for search debouncing
  useEffect(() => {
    const timeout = setTimeout(() => {
      dispatch({ type: 'SET_DEBOUNCED_SEARCH', payload: filterState.searchName });
    }, 300);
    
    return () => clearTimeout(timeout);
  }, [filterState.searchName]);

  // CRITICAL: All other useEffect patterns are in the HOOKS below
  // These hooks contain proper useEffect implementations for:
  // - Data loading and year changes (useRosterData)
  // - Cache invalidation (useAllocationCalculations) 
  // - Filter processing (useMemberFiltering)

    // Use refactored data management hooks - CONTAINS ALL DATA useEffect PATTERNS
  // Only load data when component is active to prevent unnecessary API calls
  const {
    roster,
    outcomeAllocations,
    unassignedRoles,
    outcomes,
    applications,
    availableTags,
    companyOptions,
    locationOptions,
    loading,
    error,
    basicRosterReady,
    enhancementProgress,
    refetch,
    clearCacheAndRefresh
  } = useRosterData(year, isActive, selectedDepartment || null); // Pass department for server-side filtering

  // Use allocation calculations hook - CONTAINS CACHE INVALIDATION useEffect PATTERNS
  const {
    memoizedOutcomeAllocations,
    getMemberOutcomeAllocations,
    clearCalculationCache
  } = useAllocationCalculations(roster, outcomeAllocations, year); // This hook handles data changes

  // Use member filtering hook - CONTAINS FILTER PROCESSING useEffect PATTERNS
  console.log('🔍 Roster: Calling useMemberFiltering with:', {
    roster: roster.length,
    unassignedRoles: unassignedRoles.length,
    availableTags: availableTags.length,
    memoizedOutcomeAllocations: memoizedOutcomeAllocations.size,
    filterState: Object.keys(filterState)
  });
  
  const {
    processedRosterData,
    filteredRosterData,
    finalRosterData
  } = useMemberFiltering(roster, unassignedRoles, filterState, memoizedOutcomeAllocations, availableTags);
  
  console.log('🔍 Roster: Final data for rendering:', {
    processedRosterData: processedRosterData.length,
    filteredRosterData: filteredRosterData.length,
    finalRosterData: finalRosterData.length,
    finalRosterDataSample: finalRosterData.slice(0, 2).map(m => ({ id: m.id, name: m.name, isUnassigned: m.isUnassigned }))
  });

  // Teams membership map: roster_member_id -> string[] of team names
  const [memberTeamsMap, setMemberTeamsMap] = useState(new Map());
  const [availableDepartments, setAvailableDepartments] = useState([]);
  const [itOwnerFilter, setItOwnerFilter] = useState('');
  const [itOwners, setItOwners] = useState([]);

  useEffect(() => {
    api.getDepartments()
      .then(data => setAvailableDepartments(Array.isArray(data) ? data : (data?.departments || [])))
      .catch(() => {});
  }, []);

  useEffect(() => {
    api.getOwners().then(res => setItOwners(res?.it || [])).catch(() => {});
  }, []);
  useEffect(() => {
    const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
    fetch(`${API_BASE}/api/capacity/teams`)
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (!data?.teams) return;
        const map = new Map();
        for (const team of data.teams) {
          for (const m of team.members || []) {
            if (m.archived) continue;
            const existing = map.get(m.roster_member_id) || [];
            existing.push(team.name);
            map.set(m.roster_member_id, existing);
          }
        }
        setMemberTeamsMap(map);
      })
      .catch(() => {}); // non-critical
  }, []);

  // IT Owner post-filter
  const itOwnerOutcomeNames = useMemo(() => {
    if (!itOwnerFilter) return null;
    return new Set(
      outcomes
        .filter(o => o.it_owner === itOwnerFilter)
        .map(o => o.name || '')
    );
  }, [outcomes, itOwnerFilter]);

  const displayRosterData = useMemo(() => {
    if (!itOwnerOutcomeNames) return finalRosterData;
    return finalRosterData.filter(member => {
      const memberOutcomes = memoizedOutcomeAllocations.get(member.id) || {};
      return Object.keys(memberOutcomes).some(outcomeName => itOwnerOutcomeNames.has(outcomeName));
    });
  }, [finalRosterData, itOwnerOutcomeNames, memoizedOutcomeAllocations]);

  // Create export service instance
  const exportService = useMemo(() => new RosterExportService(), []);

  // Cache style objects for better performance
  const tableStyles = useMemo(() => ({
    unassignedRow: {
      bgcolor: theme.palette.mode === 'dark' 
        ? theme.palette.warning.dark + '26'
        : theme.palette.warning.light + '26'
    },
    expandedRow: {
      bgcolor: theme.palette.mode === 'dark'
        ? theme.palette.grey[800] + '4D'
        : theme.palette.grey[100] + '80'
    }
  }), [theme]);

  // Show staffed and unassigned allocations based on filter
  const showStaffed = filterState.allocationTypeFilter.includes('staffed');
  const showUnassigned = filterState.allocationTypeFilter.includes('unassigned');

  // Format member function display
  const formatMemberFunction = useCallback((member) => {
    if (member.function === 'Developer' && member.focus) {
      return `${member.focus} Developer`;
    }
    return member.function || 'Other';
  }, []);

  // Event handlers - PROPER useCallback implementations
  const handleEditMember = useCallback((member) => {
    setEditingMember(member);
    setAddMemberModalOpen(true);
  }, []);

  const handleEditAllocations = useCallback((member, outcomes) => {
    setEditingMemberAllocations(member);
    setEditingMemberOutcomes(outcomes);
    setAllocationModalOpen(true);
  }, []);

  const handleToggleExpansion = useCallback((memberKey) => {
    setExpandedMembers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(memberKey)) {
        newSet.delete(memberKey);
      } else {
        newSet.add(memberKey);
      }
      return newSet;
    });
  }, []);

  const handleExport = useCallback(() => {
    try {
      exportService.exportToExcel(
        roster,
        outcomeAllocations,
        getMemberOutcomeAllocations,
        outcomes,
        availableTags,
        formatMemberFunction
      );
    } catch (error) {
      console.error('Error exporting roster:', error);
    }
  }, [exportService, roster, outcomeAllocations, getMemberOutcomeAllocations, outcomes, availableTags, formatMemberFunction]);

  const handleRefresh = useCallback(async () => {
    console.log('🔄 Manual refresh triggered');
    clearCalculationCache();
    await clearCacheAndRefresh();
    // Notify FVA and Outcome/Staffing pages to re-fetch with fresh rate data
    markRatesUpdated();
    window.dispatchEvent(new CustomEvent('outcomeCachesInvalidated', {
      detail: { source: 'roster_manual_refresh' }
    }));
  }, [clearCalculationCache, clearCacheAndRefresh]);

  // Simple loading state
  if (loading && !basicRosterReady) {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={onBackToHome}
            >
              Back to Home
            </Button>
              <Typography variant="h4" component="h1" fontWeight={600}>
    IT Roster
  </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="primary.main" fontWeight={500}>
                Loading roster data...
              </Typography>
            </Box>
          </Box>
        </Box>
        
        <FilterSkeleton />
        <Paper elevation={2}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Expand</TableCell>
                  <TableCell>Member</TableCell>
                  <TableCell>Actions</TableCell>
                  <TableCell>Details</TableCell>
                  <TableCell>Outcomes</TableCell>
                  <TableCell>Tags</TableCell>
                  <TableCell>Status</TableCell>
                  {MONTHS.map(month => (
                    <TableCell key={month} align="center">{month}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {[...Array(10)].map((_, i) => (
                  <MemberRowSkeleton key={i} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={onBackToHome}
          >
            Home
          </Button>
            <Typography variant="h4" component="h1" fontWeight={600}>
    IT Roster
  </Typography>
          {/* Enhanced Loading Indicators */}
          {(loading || enhancementProgress.allocations === 'loading' || enhancementProgress.unassignedRoles === 'loading' || enhancementProgress.supportingData === 'loading') && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={20} />
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body2" color="primary.main" fontWeight={500}>
                  {loading ? 'Loading roster members...' :
                   enhancementProgress.supportingData === 'loading' ? 'Loading supporting data...' :
                   enhancementProgress.allocations === 'loading' ? 'Processing allocations...' :
                   enhancementProgress.unassignedRoles === 'loading' ? 'Loading unassigned roles...' : 'Processing...'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {loading ? 'Fetching member information' :
                   enhancementProgress.supportingData === 'loading' ? 'Loading outcomes, tags, and filters' :
                   enhancementProgress.allocations === 'loading' ? 'Calculating monthly allocations' :
                   enhancementProgress.unassignedRoles === 'loading' ? 'Finding unassigned roles' : 'Almost ready...'}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddMemberModalOpen(true)}
            disabled={isReadOnly}
          >
            Add Member
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleExport}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <FilterSection
        filterState={filterState}
        dispatch={dispatch}
        companyOptions={companyOptions}
        locationOptions={locationOptions}
        availableOutcomes={outcomes.map(o => o.name || '')}
        availableApplications={applications.map(a => a.name || '')}
        availableTags={availableTags}
        availableDepartments={availableDepartments.map(d => d.name || d).filter(Boolean).sort()}
        year={year}
        setYear={setYear}
        activeFiltersCount={activeFiltersCount}
        itOwnerFilter={itOwnerFilter}
        setItOwnerFilter={setItOwnerFilter}
        itOwners={itOwners}
      />

      {/* Enhanced Results Summary */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            Showing {displayRosterData.length} members
            {activeFiltersCount > 0 && ` (${activeFiltersCount} filters active)`}
          </Typography>
          
          {/* Data Status Indicators */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label={`Roster: ${roster.length} members`}
              size="small"
              color={basicRosterReady ? 'success' : 'default'}
              variant={basicRosterReady ? 'filled' : 'outlined'}
              icon={basicRosterReady ? undefined : <CircularProgress size={12} />}
            />
            
            <Chip
              label={`Allocations: ${enhancementProgress.allocations}`}
              size="small"
              color={enhancementProgress.allocations === 'complete' ? 'success' : 
                     enhancementProgress.allocations === 'loading' ? 'primary' : 
                     enhancementProgress.allocations === 'failed' ? 'error' : 'default'}
              variant={enhancementProgress.allocations === 'complete' ? 'filled' : 'outlined'}
              icon={enhancementProgress.allocations === 'loading' ? <CircularProgress size={12} /> : undefined}
            />
            
            <Chip
              label={`Unassigned: ${enhancementProgress.unassignedRoles}`}
              size="small"
              color={enhancementProgress.unassignedRoles === 'complete' ? 'success' : 
                     enhancementProgress.unassignedRoles === 'loading' ? 'primary' : 
                     enhancementProgress.unassignedRoles === 'failed' ? 'error' : 'default'}
              variant={enhancementProgress.unassignedRoles === 'complete' ? 'filled' : 'outlined'}
              icon={enhancementProgress.unassignedRoles === 'loading' ? <CircularProgress size={12} /> : undefined}
            />
            
            <Chip
              label={`Supporting: ${enhancementProgress.supportingData}`}
              size="small"
              color={enhancementProgress.supportingData === 'complete' ? 'success' : 
                     enhancementProgress.supportingData === 'loading' ? 'primary' : 
                     enhancementProgress.supportingData === 'failed' ? 'error' : 'default'}
              variant={enhancementProgress.supportingData === 'complete' ? 'filled' : 'outlined'}
              icon={enhancementProgress.supportingData === 'loading' ? <CircularProgress size={12} /> : undefined}
            />
          </Box>
        </Box>
        
        {/* Progress Summary */}
        {!basicRosterReady || enhancementProgress.allocations !== 'complete' || enhancementProgress.unassignedRoles !== 'complete' || enhancementProgress.supportingData !== 'complete' ? (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {!basicRosterReady ? '⏳ Loading basic roster data...' :
               enhancementProgress.supportingData !== 'complete' ? '⏳ Loading filters and supporting data...' :
               enhancementProgress.allocations !== 'complete' ? '⏳ Processing allocation calculations...' :
               enhancementProgress.unassignedRoles !== 'complete' ? '⏳ Finding unassigned roles...' :
               '✅ All data loaded successfully'}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="success.main" fontWeight={500}>
              ✅ All roster data loaded • {outcomeAllocations.length} outcome allocations • {unassignedRoles.length} unassigned roles
            </Typography>
          </Box>
        )}
      </Box>

      {/* Main Table */}
      <Paper elevation={2}>
        <TableContainer>
          <Table stickyHeader size="small" sx={{ 
            '& .MuiTableCell-root': { 
              padding: '2px 4px', 
              fontSize: '0.75rem'
            }
          }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 40, width: 40 }}>Exp</TableCell>
                <TableCell sx={{ minWidth: 120, width: 120 }}>Member</TableCell>
                <TableCell sx={{ minWidth: 80, width: 80 }}>Actions</TableCell>
                <TableCell sx={{ minWidth: 180, width: 180 }}>Details</TableCell>
                <TableCell sx={{ minWidth: 140, width: 140 }}>Outcomes</TableCell>
                <TableCell sx={{ minWidth: 100, width: 100 }}>Tags</TableCell>
                <TableCell sx={{ minWidth: 110, width: 110 }}>Teams</TableCell>
                <TableCell sx={{ minWidth: 90, width: 90 }}>Status</TableCell>
                {MONTHS.map(month => (
                  <TableCell key={month} align="center" sx={{ minWidth: 50, width: 50 }}>
                    {month.substring(0, 3)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayRosterData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8 + MONTHS.length} align="center" sx={{ py: 4 }}>
                    {!basicRosterReady ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                        <CircularProgress size={20} />
                        <Typography variant="body2" color="text.secondary">
                          Loading roster members...
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No members found matching the current filters
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                displayRosterData.flatMap((member) => {
                  const rowKey = member.isUnassigned ? `unassigned-${member.unassignedRoleData?.id || member.id}` : member.id;
                  const isExpanded = expandedMembers.has(member.id);
                  const staffedAllocations = member.staffedAllocations || new Array(12).fill(0);
                  
                  const rows = [
                    <TableRow 
                      key={rowKey}
                      sx={member.isUnassigned ? tableStyles.unassignedRow : {}}
                    >
                      <TableCell align="center">
                        <IconButton
                          onClick={() => handleToggleExpansion(member.id)}
                          size="small"
                        >
                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      
                      <TableCell>
                        <Typography variant="caption" fontWeight={500} sx={{ fontSize: '0.7rem' }}>
                          {member.name || ''}
                        </Typography>
                      </TableCell>
                      
                      <TableCell align="center">
                        {member.isUnassigned ? (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleEditAllocations(member)}
                            disabled={isReadOnly}
                            sx={{ fontSize: '0.6rem', py: 0.25, px: 0.5, minWidth: 'auto' }}
                          >
                            Assign
                          </Button>
                        ) : (
                          <Box sx={{ display: 'flex', gap: 0.25, flexDirection: 'column' }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleEditMember(member)}
                              disabled={isReadOnly}
                              sx={{ fontSize: '0.55rem', py: 0.125, px: 0.25, minWidth: 'auto' }}
                            >
                              Edit
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => {
                                // Convert the memoized allocations back to the format the modal expects
                                const memberAllocations = memoizedOutcomeAllocations.get(member.id) || {};
                                
                                // The modal expects outcome_id as keys, but we have outcome_name as keys
                                // We need to find the outcome_id for each outcome_name
                                const memberOutcomesForModal = {};
                                
                                Object.keys(memberAllocations).forEach(outcomeName => {
                                  // Find the outcome by name to get its ID
                                  const outcome = outcomes.find(o => o.name === outcomeName);
                                  if (outcome) {
                                    memberOutcomesForModal[outcome.id] = {
                                      outcome_id: outcome.id,
                                      outcome_name: outcomeName,
                                      ...memberAllocations[outcomeName]
                                    };
                                  }
                                });
                                
                                console.log('🔍 Converted member outcomes for modal:', memberOutcomesForModal);
                                console.log('🔍 Member outcomes keys:', Object.keys(memberOutcomesForModal));
                                console.log('🔍 Available outcomes for lookup:', outcomes.map(o => ({ id: o.id, name: o.name })));
                                console.log('🔍 Original memoized allocations:', memberAllocations);
                                handleEditAllocations(member, memberOutcomesForModal);
                              }}
                              disabled={isReadOnly}
                              sx={{ fontSize: '0.55rem', py: 0.125, px: 0.25, minWidth: 'auto' }}
                            >
                              Alloc
                            </Button>
                          </Box>
                        )}
                      </TableCell>
                      
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                            <Chip
                              label={member.company || ''}
                              size="small"
                              color="success"
                              variant="outlined"
                              sx={{ fontSize: '0.6rem', height: 16, '& .MuiChip-label': { px: 0.5 } }}
                            />
                            <Chip
                              label={getLocationDisplayName(member.location || '').substring(0, 2)}
                              size="small"
                              color="info"
                              variant="outlined"
                              sx={{ fontSize: '0.6rem', height: 16, '& .MuiChip-label': { px: 0.5 } }}
                            />
                          </Box>
                          <Typography variant="caption" fontWeight={500} sx={{ fontSize: '0.65rem', lineHeight: 1.2 }}>
                            {formatMemberFunction(member)}
                          </Typography>
                          {member.role && (
                            member.role.length > 25 ? (
                              <Tooltip title={member.role} arrow>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', lineHeight: 1.1, cursor: 'help' }}>
                                  {member.role.substring(0, 25)}...
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', lineHeight: 1.1 }}>
                                {member.role}
                              </Typography>
                            )
                          )}
                        </Box>
                      </TableCell>
                      
                      <TableCell>
                        {member.isUnassigned ? (
                          <Chip
                            label={(member.unassignedRoleData?.outcome_name || member.outcome_name || 'Unknown').substring(0, 12) + '...'}
                            size="small"
                            color="warning"
                            variant="outlined"
                            sx={{ fontSize: '0.6rem', height: 16, '& .MuiChip-label': { px: 0.5 } }}
                          />
                        ) : member.outcomeNames?.length > 0 ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25 }}>
                            {member.outcomeNames.slice(0, 1).map((outcome, idx) => (
                              outcome.length > 15 ? (
                                <Tooltip key={idx} title={outcome} arrow>
                                  <Chip
                                    label={outcome.substring(0, 15) + '...'}
                                    size="small"
                                    color="secondary"
                                    variant="outlined"
                                    sx={{ fontSize: '0.6rem', height: 16, '& .MuiChip-label': { px: 0.5 }, cursor: 'help' }}
                                  />
                                </Tooltip>
                              ) : (
                                <Chip
                                  key={idx}
                                  label={outcome}
                                  size="small"
                                  color="secondary"
                                  variant="outlined"
                                  sx={{ fontSize: '0.6rem', height: 16, '& .MuiChip-label': { px: 0.5 } }}
                                />
                              )
                            ))}
                            {member.outcomeNames.length > 1 && (
                              <Chip
                                label={`+${member.outcomeNames.length - 1}`}
                                size="small"
                                color="secondary"
                                variant="outlined"
                                sx={{ fontSize: '0.6rem', height: 16, '& .MuiChip-label': { px: 0.5 } }}
                              />
                            )}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                            None
                          </Typography>
                        )}
                      </TableCell>
                      
                      <TableCell>
                        {member.tagNames?.size > 0 ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25 }}>
                            {Array.from(member.tagNames).slice(0, 1).map((tag, idx) => (
                              <Chip
                                key={idx}
                                label={tag.length > 8 ? tag.substring(0, 8) + '...' : tag}
                                size="small"
                                sx={{ bgcolor: (t) => t.palette.mode === 'dark' ? '#3a3a3a' : '#e0e0e0', color: (t) => t.palette.mode === 'dark' ? '#d0d0d0' : '#424242', fontSize: '0.6rem', height: 16, '& .MuiChip-label': { px: 0.5 } }}
                                variant="filled"
                              />
                            ))}
                            {member.tagNames.size > 1 && (
                              <Chip
                                label={`+${member.tagNames.size - 1}`}
                                size="small"
                                sx={{ bgcolor: (t) => t.palette.mode === 'dark' ? '#3a3a3a' : '#e0e0e0', color: (t) => t.palette.mode === 'dark' ? '#d0d0d0' : '#424242', fontSize: '0.6rem', height: 16, '& .MuiChip-label': { px: 0.5 } }}
                                variant="filled"
                              />
                            )}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                            None
                          </Typography>
                        )}
                      </TableCell>
                      
                      <TableCell>
                        {(() => {
                          const teams = memberTeamsMap.get(member.id);
                          if (!teams || teams.length === 0) {
                            return <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>—</Typography>;
                          }
                          return (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25 }}>
                              {teams.slice(0, 1).map((t, idx) => (
                                <Chip
                                  key={idx}
                                  label={t.length > 10 ? t.substring(0, 10) + '…' : t}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ fontSize: '0.6rem', height: 16, '& .MuiChip-label': { px: 0.5 } }}
                                />
                              ))}
                              {teams.length > 1 && (
                                <Tooltip title={teams.slice(1).join(', ')} arrow>
                                  <Chip
                                    label={`+${teams.length - 1}`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ fontSize: '0.6rem', height: 16, '& .MuiChip-label': { px: 0.5 }, cursor: 'help' }}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                          );
                        })()}
                      </TableCell>

                      <TableCell align="center">
                        <Chip
                          label={
                            member.allocationStatus === 'Over Allocated' ? 'Over' :
                            member.allocationStatus === 'Fully Allocated' ? 'Full' :
                            member.allocationStatus === 'Under Allocated' ? 'Under' :
                            member.allocationStatus === 'Unassigned Role' ? 'Unassigned' :
                            'None'
                          }
                          size="small"
                          color={
                            member.allocationStatus === 'Over Allocated' ? 'error' :
                            member.allocationStatus === 'Fully Allocated' ? 'success' :
                            member.allocationStatus === 'Under Allocated' ? 'warning' :
                            'default'
                          }
                          variant="outlined"
                          sx={{ fontSize: '0.6rem', height: 16, '& .MuiChip-label': { px: 0.5 } }}
                        />
                      </TableCell>
                      
                      {/* Monthly Allocation Columns */}
                      {MONTHS.map((month, idx) => (
                        <AllocationCell
                          key={idx}
                          value={staffedAllocations[idx] || 0}
                          isUnassigned={member.isUnassigned}
                        />
                      ))}
                    </TableRow>
                  ];
                  
                                    // Add expanded rows if needed - one row per outcome
                  if (isExpanded && !member.isUnassigned && member.outcomeNames && member.outcomeNames.length > 0) {
                    const memberAllocations = memoizedOutcomeAllocations.get(member.id);

                    member.outcomeNames.forEach((outcomeName, outcomeIdx) => {
                      const memberOutcomes = memberAllocations || {};
                      const outcomeData = memberOutcomes[outcomeName];
                      const outcomeAllocation = outcomeData ? outcomeData.staffed : Array(12).fill(0);
                      
                      rows.push(
                        <TableRow key={`${rowKey}-outcome-${outcomeIdx}`} sx={{ 
                          bgcolor: theme => theme.palette.mode === 'dark' 
                            ? 'rgba(144, 202, 249, 0.15)' 
                            : 'rgba(25, 118, 210, 0.08)',
                          borderLeft: theme => `3px solid ${theme.palette.mode === 'dark' ? '#90caf9' : '#1976d2'}`,
                          '&:hover': { 
                            bgcolor: theme => theme.palette.mode === 'dark' 
                              ? 'rgba(144, 202, 249, 0.25)' 
                              : 'rgba(25, 118, 210, 0.12)'
                          }
                        }}>
                          {/* Empty expand cell */}
                          <TableCell sx={{ p: '2px !important' }} />
                          
                          {/* Visual indicator in member cell */}
                          <TableCell sx={{ p: '2px !important' }}>
                            <Typography variant="caption" sx={{ 
                              fontSize: '0.6rem', 
                              color: theme => theme.palette.mode === 'dark' ? '#90caf9' : 'primary.main', 
                              fontWeight: 600 
                            }}>
                              ↳
                            </Typography>
                          </TableCell>
                          
                          {/* Empty action cell */}
                          <TableCell sx={{ p: '2px !important' }} />
                          
                          {/* Empty details cell */}
                          <TableCell sx={{ p: '2px !important' }} />
                          
                          {/* Outcome name in outcomes column */}
                          <TableCell>
                            {outcomeName.length > 18 ? (
                              <Tooltip title={outcomeName} arrow>
                                <Typography variant="caption" sx={{ fontSize: '0.65rem', fontStyle: 'italic', color: 'text.secondary', cursor: 'help' }}>
                                  └ {outcomeName.substring(0, 18)}...
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Typography variant="caption" sx={{ fontSize: '0.65rem', fontStyle: 'italic', color: 'text.secondary' }}>
                                └ {outcomeName}
                              </Typography>
                            )}
                          </TableCell>
                          
                          {/* Empty tags cell */}
                          <TableCell sx={{ p: '2px !important' }} />

                          {/* Empty teams cell */}
                          <TableCell sx={{ p: '2px !important' }} />

                          {/* Empty status cell */}
                          <TableCell sx={{ p: '2px !important' }} />
                          
                          {/* Monthly allocation columns for this outcome */}
                          {MONTHS.map((month, monthIdx) => {
                            const allocationValue = outcomeAllocation[monthIdx];
                            const displayValue = typeof allocationValue === 'number' ? Math.round(allocationValue) : allocationValue;
                            
                            return (
                              <TableCell key={monthIdx} align="center" sx={{ p: '2px !important' }}>
                                <Chip
                                  label={displayValue > 0 ? `${displayValue}%` : '-'}
                                  size="small"
                                  color={
                                    displayValue > 100 ? 'error' :
                                    displayValue === 100 ? 'success' :
                                    displayValue > 0 ? 'warning' : 'default'
                                  }
                                  variant="filled"
                                  sx={{ 
                                    minWidth: 30,
                                    height: 14,
                                    fontSize: '0.55rem',
                                    '& .MuiChip-label': { px: 0.25 },
                                    opacity: 1,
                                    bgcolor: theme => {
                                      if (displayValue > 100) return theme.palette.error.main;
                                      if (displayValue === 100) return theme.palette.success.main;
                                      if (displayValue > 0) {
                                        return theme.palette.mode === 'dark' 
                                          ? theme.palette.warning.light 
                                          : theme.palette.warning.main;
                                      }
                                      return theme.palette.mode === 'dark' 
                                        ? theme.palette.grey[700] 
                                        : theme.palette.grey[300];
                                    },
                                    color: theme => {
                                      if (displayValue > 100) return theme.palette.error.contrastText;
                                      if (displayValue === 100) return theme.palette.success.contrastText;
                                      if (displayValue > 0) {
                                        return theme.palette.mode === 'dark' 
                                          ? theme.palette.common.black 
                                          : theme.palette.common.white;
                                      }
                                      return theme.palette.mode === 'dark' 
                                        ? theme.palette.grey[300] 
                                        : theme.palette.grey[700];
                                    }
                                  }}
                                />
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    });
                  }
                  
                  return rows;
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Modals */}
      <Suspense fallback={<CircularProgress />}>
        <AddMemberModal
          open={addMemberModalOpen}
          onClose={() => {
            setAddMemberModalOpen(false);
            setEditingMember(null);
          }}
          onMemberAdded={() => {
            refetch('roster');
            setAddMemberModalOpen(false);
            setEditingMember(null);
          }}
          editingMember={editingMember}
          companyOptions={companyOptions}
          locationOptions={locationOptions}
          functionOptions={FUNCTION_OPTIONS}
          focusOptions={DEVELOPER_FOCUS_OPTIONS}
          availableTags={availableTags}
        />
        
        {allocationModalOpen && (
          <AllocationEditingModal
            open={allocationModalOpen}
            onClose={() => {
              setAllocationModalOpen(false);
              setEditingMemberAllocations(null);
              setEditingMemberOutcomes(null);
            }}
            member={editingMemberAllocations}
            memberOutcomes={editingMemberOutcomes}
            onAllocationUpdated={async () => {
              // Refresh the roster data after allocation changes
              console.log('🔄 Roster: Allocation updated, refreshing data...');
              
              // Clear calculation cache first to ensure fresh calculations
              clearCalculationCache();
              
              // Refresh allocation data from the server
              await refetch('allocations');
              
              // Also refresh unassigned roles as they might have changed
              await refetch('unassigned');
              
              console.log('✅ Roster: Data refresh completed after allocation update');
            }}
            isReadOnly={isReadOnly}
          />
        )}
      </Suspense>
    </Box>
  );
}

export default Roster;